import React, { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { CalendarRange, X } from "lucide-react";
import {
  format, startOfWeek, endOfWeek, addWeeks, startOfMonth, endOfMonth,
} from "date-fns";

const fmt = (d) => (d ? format(d, "d MMM") : "");

function buildPresets() {
  const now = new Date();
  const ws = { weekStartsOn: 1 };
  return [
    { key: "week", label: "This week", range: { from: startOfWeek(now, ws), to: endOfWeek(now, ws) } },
    { key: "next2", label: "Next 2 weeks", range: { from: startOfWeek(now, ws), to: endOfWeek(addWeeks(now, 1), ws) } },
    { key: "month", label: "This month", range: { from: startOfMonth(now), to: endOfMonth(now) } },
  ];
}

export function DateRangePicker({ value, onChange, testid = "date-range-picker", align = "start", presets: customPresets, emptyLabel = "Any date" }) {
  const [open, setOpen] = useState(false);
  const presets = customPresets || buildPresets();
  const label = value?.from
    ? value.to ? `${fmt(value.from)} – ${fmt(value.to)}` : fmt(value.from)
    : emptyLabel;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button data-testid={testid} className={`inline-flex items-center gap-2 rounded-full border px-3.5 py-1.5 text-xs font-medium transition-colors ${value?.from ? "bg-[#EDE7FF] text-[#5b2fd6] border-[#d9ccff]" : "bg-white text-[#595D62] border-[#EEEFEF] hover:bg-[#F6F6FD]"}`}>
          <CalendarRange className="h-3.5 w-3.5" /> {label}
          {value?.from && (
            <span role="button" tabIndex={0} data-testid={`${testid}-clear`} onClick={(e) => { e.stopPropagation(); onChange(undefined); }} className="ml-0.5 rounded-full hover:bg-white/60 p-0.5"><X className="h-3 w-3" /></span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align={align} className="w-auto p-0 bg-white border-[#EEEFEF]" data-testid={`${testid}-popover`}>
        <div className="flex flex-col sm:flex-row">
          <div className="flex sm:flex-col gap-1 p-3 border-b sm:border-b-0 sm:border-r border-[#f0f0f3] min-w-[9rem]">
            <p className="hidden sm:block font-mono-data text-[9px] uppercase tracking-wider text-[#9FA1A4] px-2 pb-1">Quick ranges</p>
            {presets.map((p) => (
              <button key={p.key} data-testid={`${testid}-preset-${p.key}`} onClick={() => { onChange(p.range); }} className="text-left rounded-lg px-2.5 py-1.5 text-xs text-[#595D62] hover:bg-[#F6F6FD] hover:text-[#06060C] transition-colors">{p.label}</button>
            ))}
            <button data-testid={`${testid}-preset-clear`} onClick={() => { onChange(undefined); setOpen(false); }} className="text-left rounded-lg px-2.5 py-1.5 text-xs text-[#c4c5cc] hover:bg-[#F6F6FD] hover:text-rose-600 transition-colors">Clear</button>
          </div>
          <Calendar
            mode="range"
            numberOfMonths={2}
            selected={value}
            onSelect={onChange}
            weekStartsOn={1}
            defaultMonth={value?.from}
            className="p-3"
          />
        </div>
      </PopoverContent>
    </Popover>
  );
}
